import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import {
  badgeCreditedToLocal,
  DEFAULT_BADGE_DATE_ADDED,
  FeedUpdate,
  feedUpdateKey,
  mergeProfileSameDayAutoGrants,
} from 'features/feed/useFeedUpdates';
import { Profile } from 'features/profiles/useProfiles';
import { badgeIdsInJourney, Journey } from 'features/catalog/badge-journeys';
import { unreleasedLockLine } from 'features/catalog/badge-release';
import { formatLastUpdated, talosBadgesLogoURL } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { pathForNav } from 'shared/lib/nav-paths';
import {
  feedCutoff,
  getFeedFilter,
  patchFeedFilter,
  PROFILE_FEED_FILTER,
  resetFeedFilter,
  setFeedFilter,
  subscribeFeedFilter,
  type FeedFilterState,
  type FeedRarityFilter,
  type FeedTimeRange,
  type FeedTopicFilter,
} from 'features/feed/feed-filter';
import { clearPendingFeedScroll, getPendingFeedScroll, setPendingFeedScroll } from 'features/feed/feed-scroll-target';
import {
  getProfileSearchSort,
  profileIdsForSearchSort,
  subscribeProfileSearchSort,
} from 'features/profiles/profile-search-sort';
import { compareBadgesByScoreAsc, rarityOrderIndex } from 'features/catalog/rarity';
import '../gallery/BadgeGalleryView.css';
import { BadgeComponent } from 'shared/components/BadgeComponent';
import { FeedAside } from 'features/feed/FeedAside';
import './FeedAside.css';
import './FeedComponent.css';
import { ProfileIdentitySearch } from 'features/profiles/ProfileIdentitySearch';

const scrollFeedCardIntoView = (card: HTMLElement) => {
  const scroller =
    document.querySelector('.feed-layout .feed-page') ??
    document.querySelector('.profile-collected-body') ??
    document.querySelector('.app-shell-main');
  if (scroller instanceof HTMLElement) {
    const top =
      card.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop -
      scroller.clientHeight / 2 +
      card.clientHeight / 2;
    scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    return;
  }
  card.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });
};

/** Keep sticky aside from jumping when filter shrinks the feed while scrolled. */
const scrollFeedMainToTop = () => {
  const feedPage = document.querySelector('.feed-layout .feed-page');
  if (feedPage instanceof HTMLElement) {
    feedPage.scrollTo({ top: 0, behavior: 'auto' });
    return;
  }
  const scrollable = document.querySelector('.app-shell-main');
  if (scrollable) scrollable.scrollTo({ top: 0, behavior: 'auto' });
};

/** Survives Badges Feed unmount for navigate-back freshness checks. */
const FEED_NAV_STALE_MS = 2 * 60 * 1000;
const FEED_IDLE_REFRESH_MS = 10 * 60 * 1000;
let lastBadgesFeedSyncAt = Date.now();

interface FeedComponentProps {
  currentProfile: Profile | null;
  feedFocusId?: string | null;
  onFeedFocusChange?: (profileId: string | null) => void;
  setSelectedProfileID: (email: string | null) => void;
  setSelectedBadge?: (badge: Badge | null) => void;
  /** Lock the feed to one profile (profile page Feed pill). Hides aside. */
  lockedProfileId?: string | null;
  /** Highlight matching badges in the scrolling backdrop while a feed card is hovered. */
  onBackdropHighlight?: (badgeIds: string[] | null) => void;
  /** Re-fetch profiles/submissions from the sheet (rebuilds this feed). */
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
}

const hasRarePlus = (update: FeedUpdate): boolean =>
  update.badgesByRarity.mythic.length + update.badgesByRarity.legendary.length + update.badgesByRarity.rare.length > 0;

const hasLegendaryPlus = (update: FeedUpdate): boolean =>
  update.badgesByRarity.mythic.length + update.badgesByRarity.legendary.length > 0;

const allNewBadges = (update: FeedUpdate): Badge[] => update.sortedNewBadges;

/** Feed updates are built from the catalog map (no `badgeScore`). Prefer live copies and rank coolest-first. */
const rankBadgesByScore = (badges: Badge[], badgesMap: Map<string, Badge>): Badge[] =>
  badges.map((badge) => badgesMap.get(badge.id) ?? badge).sort(compareBadgesByScoreAsc);

const badgeIdsInJourneys = (journeys: Journey[]): Set<string> => {
  const ids = new Set<string>();
  for (const journey of journeys) {
    for (const id of badgeIdsInJourney(journey)) ids.add(id);
  }
  return ids;
};

/** Rarity chip vs journey quality (legendary+ / rare+). */
const journeysMatchingRarity = (journeys: readonly Journey[], rarity: FeedRarityFilter): Journey[] => {
  if (rarity === 'legendary') {
    return journeys.filter((journey) => rarityOrderIndex(journey.rarity) <= rarityOrderIndex('legendary'));
  }
  if (rarity === 'rare') {
    return journeys.filter((journey) => rarityOrderIndex(journey.rarity) <= rarityOrderIndex('rare'));
  }
  return [...journeys];
};

const badgesForFilter = (update: FeedUpdate, filter: FeedFilterState, badgesMap: Map<string, Badge>): Badge[] => {
  const badges = rankBadgesByScore(allNewBadges(update), badgesMap);

  // Journeys chip: rarity applies to journey quality only; show every claimed badge on those journeys.
  if (filter.topic === 'journeys') {
    const journeys = journeysMatchingRarity(update.completedJourneys, filter.rarity);
    const relatedIds = badgeIdsInJourneys(journeys);
    return badges.filter((badge) => relatedIds.has(badge.id));
  }

  if (filter.rarity === 'legendary') {
    return rankBadgesByScore([...update.badgesByRarity.mythic, ...update.badgesByRarity.legendary], badgesMap);
  }
  if (filter.rarity === 'rare') {
    return rankBadgesByScore(
      [...update.badgesByRarity.mythic, ...update.badgesByRarity.legendary, ...update.badgesByRarity.rare],
      badgesMap
    );
  }

  return badges;
};

const defaultCatalogEventAt = (): Date => {
  const [y, m, d] = DEFAULT_BADGE_DATE_ADDED.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
};

const narrowCatalogToCredits = (
  update: FeedUpdate,
  localPart: string,
  badgesMap: Map<string, Badge>
): FeedUpdate | null => {
  const ids = update.newBadges.filter((id) => {
    const badge = badgesMap.get(id);
    return badge ? badgeCreditedToLocal(badge, localPart) : false;
  });
  if (ids.length === 0) return null;
  const mythic = update.badgesByRarity.mythic.filter((b) => ids.includes(b.id));
  const legendary = update.badgesByRarity.legendary.filter((b) => ids.includes(b.id));
  const rare = update.badgesByRarity.rare.filter((b) => ids.includes(b.id));
  const common = update.badgesByRarity.common.filter((b) => ids.includes(b.id));
  return {
    ...update,
    newBadges: ids,
    badgesByRarity: { mythic, legendary, rare, common },
    sortedNewBadges: [...mythic, ...legendary, ...rare, ...common].sort(compareBadgesByScoreAsc),
  };
};

const filterKey = (filter: FeedFilterState): string =>
  `${filter.range ?? 'all'}-${filter.rarity ?? 'none'}-${filter.topic ?? 'plain'}`;

/** How many tiny badge thumbs to show in a feed row before `(+N)` on a wide card. */
const FEED_TINY_BADGE_CAP = 6;
/** Matches `.badge.tiny` / `.feed-more-badge` width when measuring an empty row. */
const FEED_TINY_BADGE_REM = 5.05;

/** Overflow count styled like the edit-screen “hidden from filters” / “hidden” mini tile. */
const FeedMoreCount: React.FC<{
  count: number;
  expanded: boolean;
  onToggle: () => void;
}> = ({ count, expanded, onToggle }) => {
  if (count <= 1) return null;
  return (
    <div
      className={`feed-more-badge${expanded ? ' is-expanded' : ''}`}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={`${count} more badges. ${expanded ? 'Collapse' : 'Expand'} to ${expanded ? 'hide' : 'review'}.`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }
      }}
    >
      <span className="feed-more-badge-tile">
        <span className="feed-more-badge-count">+{count}</span>
        {expanded ? <span className="feed-more-badge-label">hide</span> : null}
      </span>
    </div>
  );
};

/**
 * Single-row tiny badges: coolest-first (badgeScore), fit what the card width allows,
 * and keep `+N` as the last cell on that row (never wrap until expanded). Narrower
 * cards show fewer thumbs / an earlier +more. Clicking `+N` reveals the rest in the
 * same score order, same as the edit-page “hidden” tile.
 */
const FeedCompactBadgeRow: React.FC<{
  badges: Badge[];
  onSelect?: (badge: Badge) => void;
}> = ({ badges, onSelect }) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [slots, setSlots] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;

    const styles = getComputedStyle(row);
    const gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
    const sample =
      (row.querySelector('.feed-badge-wrap') as HTMLElement | null) ??
      (row.querySelector('.feed-more-badge') as HTMLElement | null);

    let cellW: number;
    if (sample) {
      cellW = sample.getBoundingClientRect().width;
    } else {
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      cellW = FEED_TINY_BADGE_REM * rootPx;
    }

    if (row.clientWidth <= 0 || cellW <= 0) return;
    setSlots(Math.max(1, Math.floor((row.clientWidth + gap) / (cellW + gap))));
  }, []);

  useLayoutEffect(() => {
    if (badges.length === 0) {
      setSlots(null);
      return;
    }
    measure();
    const row = rowRef.current;
    if (!row) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(row);
    return () => observer.disconnect();
  }, [badges.length, measure]);

  const ranked = useMemo(() => [...badges].sort(compareBadgesByScoreAsc), [badges]);

  const { visible, overflow } = useMemo(() => {
    if (ranked.length === 0) return { visible: [] as Badge[], overflow: 0 };

    // Before measure, assume the wide-card cap so we don't flash a huge row.
    const available = slots ?? FEED_TINY_BADGE_CAP;

    if (ranked.length <= available && ranked.length <= FEED_TINY_BADGE_CAP) {
      return { visible: ranked, overflow: 0 };
    }

    // Reserve the last slot for +N whenever anything is hidden (width or cap).
    const thumbCount = Math.max(0, Math.min(FEED_TINY_BADGE_CAP, available - 1, ranked.length - 1));
    const overflow = ranked.length - thumbCount;
    // A single leftover always fits in the reserved +N slot — show the badge, not +1.
    if (overflow <= 1) {
      return { visible: ranked, overflow: 0 };
    }
    return {
      visible: ranked.slice(0, thumbCount),
      overflow,
    };
  }, [ranked, slots]);

  useEffect(() => {
    if (overflow === 0) setExpanded(false);
  }, [overflow]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  if (ranked.length === 0) return null;

  const hidden = ranked.slice(visible.length);

  return (
    <div className={`feed-badge-row compact${expanded ? ' is-expanded' : ''}`} ref={rowRef}>
      {visible.map((badge) => (
        <FeedBadge key={badge.id} badge={badge} onSelect={onSelect} />
      ))}
      <FeedMoreCount count={overflow} expanded={expanded} onToggle={toggleExpanded} />
      {expanded ? hidden.map((badge) => <FeedBadge key={badge.id} badge={badge} onSelect={onSelect} />) : null}
    </div>
  );
};

const FeedBadge: React.FC<{
  badge: Badge;
  onSelect?: (badge: Badge) => void;
}> = ({ badge, onSelect }) => (
  <div
    className="feed-badge-wrap"
    onClick={(e) => {
      e.stopPropagation();
    }}
  >
    <img src={badge.rarityImageUrl} alt="" className="feed-badge-rarity" aria-hidden />
    <BadgeComponent badge={badge} tiny blackAndWhite={false} onClick={(b) => onSelect?.(b)} />
  </div>
);

/** Resolve credit local-parts → display names (falls back to email/local). */
const creditNamesFromField = (field: string | undefined, profilesMap: Map<string, Profile>): string | null => {
  if (!field) return null;
  const locals: string[] = [];
  const re = /([^\s,@]+)@/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(field)) !== null) {
    locals.push(match[1]);
  }
  if (locals.length === 0) return field;
  return locals.map((local) => profilesMap.get(local)?.name ?? local).join(', ');
};

/** Match Modal credit line: ✏️ name, plus 🎨 design when present (or ✏️ & 🎨 when same). */
const badgeCreditsLabel = (badge: Badge, profilesMap: Map<string, Profile>): string | null => {
  const idea = creditNamesFromField(badge.credits, profilesMap);
  const design = creditNamesFromField(badge.design, profilesMap);
  if (!idea && !design) return null;
  if (idea && !design) return `✏️ & 🎨 ${idea}`;
  if (!idea && design) return `🎨 ${design}`;
  if (idea === design) return `✏️ & 🎨 ${idea}`;
  return `✏️ ${idea} · 🎨 ${design}`;
};

const FeedBadgeExplain: React.FC<{
  badge: Badge;
  onSelect?: (badge: Badge) => void;
  profilesMap?: Map<string, Profile>;
  showCredits?: boolean;
}> = ({ badge, onSelect, profilesMap, showCredits = false }) => {
  const creditsLabel = showCredits && profilesMap ? badgeCreditsLabel(badge, profilesMap) : null;
  const releaseLine = unreleasedLockLine(badge.id);

  return (
    <div className="feed-single-body">
      <FeedBadge badge={badge} onSelect={onSelect} />
      <button
        type="button"
        className={`feed-badge-copy rarity-${badge.rarityName}`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(badge);
        }}
      >
        <p className="feed-badge-copy-name">
          <span className={`feed-status-dot rarity-${badge.rarityName}`} />
          <span>{badge.name}</span>
        </p>
        {releaseLine ? (
          <p className="feed-badge-copy-desc">
            <i>{releaseLine}</i>
          </p>
        ) : badge.description ? (
          <p className="feed-badge-copy-desc">{badge.description}</p>
        ) : null}
        {creditsLabel ? <p className="feed-badge-copy-credits">{creditsLabel}</p> : null}
      </button>
    </div>
  );
};

/** Name in a feed headline — always opens that person's Collected. */
const FeedProfileName: React.FC<{
  name: string;
  profileId: string;
  onOpen: (profileId: string, e: React.MouseEvent) => void;
}> = ({ name, profileId, onOpen }) => (
  <button type="button" className="feed-profile-name" onClick={(e) => onOpen(profileId, e)}>
    {name}
  </button>
);

/** Badge name in a feed headline — opens the badge modal. */
const FeedBadgeName: React.FC<{
  badge: Badge;
  onSelect?: (badge: Badge) => void;
}> = ({ badge, onSelect }) => (
  <button
    type="button"
    className="feed-headline-badge-name"
    onClick={(e) => {
      e.stopPropagation();
      onSelect?.(badge);
    }}
  >
    {badge.name}
  </button>
);

/** System auto-grant headline: Talos Badges granted Name the Badge badge — or “to N profiles” when 4+. */
const joinRecipientNames = (names: React.ReactNode[]): React.ReactNode => {
  if (names.length <= 1) return names[0] ?? null;
  if (names.length === 2) {
    return (
      <>
        {names[0]} and {names[1]}
      </>
    );
  }
  return (
    <>
      {names.slice(0, -1).map((name, i) => (
        <React.Fragment key={i}>
          {/* Keep "Name," together so the comma never wraps alone onto the next line. */}
          <span className="feed-recipient-chunk">{name},</span>
          {i < names.length - 2 ? ' ' : ' and '}
        </React.Fragment>
      ))}
      {names[names.length - 1]}
    </>
  );
};

const autoGrantHeadline = (
  profileName: React.ReactNode,
  badges: Badge[],
  onSelect?: (badge: Badge) => void,
  recipientCount = 1
): React.ReactNode => {
  if (recipientCount >= 4) {
    const toProfiles = (
      <>
        to <strong>{recipientCount} profiles</strong>
      </>
    );
    if (badges.length === 1) {
      return (
        <>
          <strong>Talos Badges</strong> granted the <FeedBadgeName badge={badges[0]} onSelect={onSelect} /> badge{' '}
          {toProfiles}
        </>
      );
    }
    if (badges.length === 2) {
      return (
        <>
          <strong>Talos Badges</strong> granted the <FeedBadgeName badge={badges[0]} onSelect={onSelect} /> and{' '}
          <FeedBadgeName badge={badges[1]} onSelect={onSelect} /> badges {toProfiles}
        </>
      );
    }
    if (badges.length === 3) {
      return (
        <>
          <strong>Talos Badges</strong> granted the <FeedBadgeName badge={badges[0]} onSelect={onSelect} />,{' '}
          <FeedBadgeName badge={badges[1]} onSelect={onSelect} />, and{' '}
          <FeedBadgeName badge={badges[2]} onSelect={onSelect} /> badges {toProfiles}
        </>
      );
    }
    return (
      <>
        <strong>Talos Badges</strong> granted <strong>{badges.length} new badges</strong> {toProfiles}
      </>
    );
  }

  if (badges.length === 1) {
    return (
      <>
        <strong>Talos Badges</strong> granted {profileName} the <FeedBadgeName badge={badges[0]} onSelect={onSelect} />{' '}
        badge
      </>
    );
  }
  if (badges.length === 2) {
    return (
      <>
        <strong>Talos Badges</strong> granted {profileName} the <FeedBadgeName badge={badges[0]} onSelect={onSelect} />{' '}
        and <FeedBadgeName badge={badges[1]} onSelect={onSelect} /> badges
      </>
    );
  }
  if (badges.length === 3) {
    return (
      <>
        <strong>Talos Badges</strong> granted {profileName} the <FeedBadgeName badge={badges[0]} onSelect={onSelect} />,{' '}
        <FeedBadgeName badge={badges[1]} onSelect={onSelect} />, and{' '}
        <FeedBadgeName badge={badges[2]} onSelect={onSelect} /> badges
      </>
    );
  }
  return (
    <>
      <strong>Talos Badges</strong> granted {profileName} <strong>{badges.length} new badges</strong>
    </>
  );
};

/** Catalog drop headline: Talos Badges Gallery: N new badge(s) added. */
const catalogAdditionHeadline = (count: number): React.ReactNode => (
  <>
    <Link
      to={pathForNav('badge_gallery', null)}
      className="feed-headline-badge-name"
      onClick={(e) => e.stopPropagation()}
    >
      Talos Badges Gallery
    </Link>
    : {count} new badge{count === 1 ? '' : 's'} added
  </>
);

const FeedCardTopline: React.FC<{
  headline: React.ReactNode;
  timeAgo: string;
  withLogo?: boolean;
}> = ({ headline, timeAgo, withLogo = false }) => (
  <div className="feed-card-topline">
    {withLogo ? (
      <div className="feed-catalog-headline">
        <img src={talosBadgesLogoURL} alt="" className="feed-catalog-logo" />
        <p className="feed-card-headline">{headline}</p>
      </div>
    ) : (
      <p className="feed-card-headline">{headline}</p>
    )}
    <span className="feed-timestamp">{timeAgo}</span>
  </div>
);

export const FeedComponent: React.FC<FeedComponentProps> = ({
  currentProfile,
  feedFocusId: feedFocusIdProp = null,
  onFeedFocusChange,
  setSelectedProfileID,
  setSelectedBadge,
  lockedProfileId = null,
  onBackdropHighlight,
  onRefresh,
  isRefreshing = false,
}) => {
  const { feedUpdates, profilesMap, rankedProfiles, badgesMap } = useTalosData();
  const navigate = useNavigate();
  const [filter, setFilterState] = useState<FeedFilterState>(() => getFeedFilter());
  useEffect(() => subscribeFeedFilter(() => setFilterState(getFeedFilter())), []);
  const [feedFocusIdInternal, setFeedFocusIdInternal] = useState<string | null>(null);
  const feedFocusId = lockedProfileId ?? (onFeedFocusChange ? feedFocusIdProp : feedFocusIdInternal);
  const setFeedFocusId = onFeedFocusChange ?? setFeedFocusIdInternal;
  const isProfileScoped = !!lockedProfileId;
  const [refreshDone, setRefreshDone] = useState(false);
  const wasRefreshingRef = useRef(false);
  const [feedSyncEpoch, setFeedSyncEpoch] = useState(0);

  useEffect(() => {
    if (isRefreshing) {
      wasRefreshingRef.current = true;
      setRefreshDone(false);
      return;
    }
    if (!wasRefreshingRef.current) return;
    wasRefreshingRef.current = false;
    lastBadgesFeedSyncAt = Date.now();
    setFeedSyncEpoch((n) => n + 1);
    setRefreshDone(true);
    const timer = window.setTimeout(() => setRefreshDone(false), 2_000);
    return () => window.clearTimeout(timer);
  }, [isRefreshing]);

  // Navigate to feed: refresh if >2m since last sync.
  // Stay on feed: refresh every 10m if they didn't click Refresh (manual click resets via feedSyncEpoch).
  useEffect(() => {
    if (!onRefresh || isProfileScoped) return;

    if (Date.now() - lastBadgesFeedSyncAt >= FEED_NAV_STALE_MS) {
      lastBadgesFeedSyncAt = Date.now();
      void onRefresh();
    }

    const remaining = FEED_IDLE_REFRESH_MS - (Date.now() - lastBadgesFeedSyncAt);
    const timer = window.setTimeout(
      () => {
        lastBadgesFeedSyncAt = Date.now();
        void onRefresh();
        setFeedSyncEpoch((n) => n + 1);
      },
      remaining > 0 ? remaining : FEED_IDLE_REFRESH_MS
    );
    return () => window.clearTimeout(timer);
  }, [onRefresh, isProfileScoped, feedSyncEpoch]);

  const rangeCutoff = feedCutoff(filter.range);
  const pendingActivityScrollKeyRef = useRef<string | null>(null);
  const pendingActivityFallbackBadgeRef = useRef<Badge | null>(null);
  const [activityScrollNonce, setActivityScrollNonce] = useState(0);
  const ownId = currentProfile ? getFirstPartOfEmail(currentProfile.email) : null;

  useEffect(() => () => onBackdropHighlight?.(null), [onBackdropHighlight]);

  // Badges Feed: default filters while mounted; clear on leave. Profile: All time.
  useEffect(() => {
    if (isProfileScoped) {
      setFeedFilter(PROFILE_FEED_FILTER);
      return;
    }
    resetFeedFilter();
    return () => {
      resetFeedFilter();
    };
  }, [isProfileScoped, lockedProfileId]);

  const cardHighlightProps = (update: FeedUpdate) => {
    if (!onBackdropHighlight) return {};
    return {
      onMouseEnter: () => onBackdropHighlight(allNewBadges(update).map((badge) => badge.id)),
      onMouseLeave: () => onBackdropHighlight(null),
    };
  };

  const applyFilterPatch = (patch: Partial<FeedFilterState>) => {
    scrollFeedMainToTop();
    patchFeedFilter(patch);
  };

  const setTimeRange = (range: Exclude<FeedTimeRange, null>) => {
    if (isProfileScoped) {
      // Profile: no All chip — toggle off returns to All (null).
      applyFilterPatch({ range: filter.range === range ? null : range });
      return;
    }
    // Badges Feed: always Last Week or In <year>.
    applyFilterPatch({ range });
  };

  const setRarity = (rarity: Exclude<FeedRarityFilter, null>) => {
    applyFilterPatch({ rarity: filter.rarity === rarity ? null : rarity });
  };

  const setTopic = (topic: Exclude<FeedTopicFilter, null>) => {
    applyFilterPatch({ topic: filter.topic === topic ? null : topic });
  };

  const filtered = useMemo(() => {
    const defaultCatalogMs = defaultCatalogEventAt().getTime();
    const results: FeedUpdate[] = [];

    // useFeedUpdates guarantees newest-first ordering.
    for (const update of feedUpdates) {
      let next: FeedUpdate = update;

      if (isProfileScoped) {
        if (!feedFocusId) continue;
        if (update.isCatalogAddition) {
          // Talos “new badges added” cards, narrowed to this person’s idea credits.
          const narrowed = narrowCatalogToCredits(update, feedFocusId, badgesMap);
          if (!narrowed) continue;
          next = narrowed;
        } else if (getFirstPartOfEmail(update.profile.email) !== feedFocusId) {
          const inGrantGroup = update.grantedToProfiles?.some((p) => getFirstPartOfEmail(p.email) === feedFocusId);
          if (!inGrantGroup) continue;
        }
      } else if (filter.topic === 'newBadges') {
        if (!update.isCatalogAddition) continue;
      } else if (update.isCatalogAddition && update.eventAt.getTime() === defaultCatalogMs) {
        // March-2024 default bucket stays behind the New Badges pill on Badges Feed.
        continue;
      }

      if (rangeCutoff && next.eventAt < rangeCutoff) continue;
      // Profile feed has no filter chips — show full activity (time already cleared to All).
      if (!isProfileScoped) {
        if (filter.topic === 'journeys') {
          // Rarity chip gates journey quality only (not badge rarity on the card).
          if (next.isCatalogAddition) continue;
          // Multi-person grants can't attribute a journey to a specific recipient.
          if ((next.grantedToProfiles?.length ?? 0) > 1) continue;
          if (journeysMatchingRarity(next.completedJourneys, filter.rarity).length === 0) continue;
        } else {
          if (filter.rarity === 'rare' && !hasRarePlus(next)) continue;
          if (filter.rarity === 'legendary' && !hasLegendaryPlus(next)) continue;
        }
      }

      results.push(next);
    }

    if (isProfileScoped && feedFocusId) {
      const focusProfile = profilesMap.get(feedFocusId);
      if (focusProfile) return mergeProfileSameDayAutoGrants(results, badgesMap, focusProfile);
    }

    return results;
  }, [feedUpdates, filter, feedFocusId, isProfileScoped, rangeCutoff, badgesMap, profilesMap]);

  /** Leaderboard / last-updated order for ←/→ cycling into profile feeds. */
  const [profileSort, setProfileSort] = useState(() => getProfileSearchSort());
  useEffect(() => subscribeProfileSearchSort(() => setProfileSort(getProfileSearchSort())), []);

  const feedCycleIds = useMemo(
    () => profileIdsForSearchSort(profilesMap.values(), profileSort, rankedProfiles),
    [profilesMap, profileSort, rankedProfiles]
  );

  const focusedProfile = feedFocusId ? (profilesMap.get(feedFocusId) ?? null) : null;

  const feedSubtitle = useMemo(() => {
    const whose =
      isProfileScoped && focusedProfile ? (ownId && feedFocusId === ownId ? 'your' : `${focusedProfile.name}'s`) : null;
    const rangeYear = new Date().getFullYear();
    const rangeSuffix =
      filter.range === 'week' ? 'since last week' : filter.range === 'year' ? `in ${rangeYear}` : 'total';

    let countLabel: string;
    if (filter.topic === 'newBadges') {
      // Catalog cards group by dateAdded — count badges shown, not cards.
      const badgeCount = filtered.reduce((n, update) => n + badgesForFilter(update, filter, badgesMap).length, 0);
      countLabel = `${badgeCount} new badge${badgeCount === 1 ? '' : 's'}`;
    } else if (filter.topic === 'journeys') {
      const journeyCount = filtered.reduce(
        (n, update) => n + journeysMatchingRarity(update.completedJourneys, filter.rarity).length,
        0
      );
      countLabel = `${journeyCount} journey completion${journeyCount === 1 ? '' : 's'}`;
    } else {
      const qualifiers: string[] = [];
      if (filter.rarity === 'rare') qualifiers.push('rare+');
      if (filter.rarity === 'legendary') qualifiers.push('legendary+');
      const qualifier = qualifiers.length ? `${qualifiers.join(' ')} ` : '';
      const count = filtered.length;
      countLabel = `${count} ${qualifier}update${count === 1 ? '' : 's'}`;
    }

    if (whose === 'your') return `${countLabel} ${rangeSuffix}`;
    if (whose && focusedProfile) return `${focusedProfile.name}: ${countLabel} ${rangeSuffix}`;
    return `${countLabel} ${rangeSuffix}`;
  }, [filter, focusedProfile, feedFocusId, ownId, isProfileScoped, filtered, badgesMap]);

  /** Name click → that person's Collected. */
  const openProfileCollected = useCallback(
    (profileId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedProfileID(profileId);
      navigate(`/${profileId}`);
    },
    [navigate, setSelectedProfileID]
  );

  /** Badges Feed: open that person's profile Feed tab. */
  const openProfileFeed = useCallback(
    (id: string) => {
      setFeedFocusId(id);
      setSelectedProfileID(id);
      navigate(`/${id}/feed`);
    },
    [navigate, setFeedFocusId, setSelectedProfileID]
  );

  /** Recent activity aside: open that profile’s feed and scroll to the matching card. */
  const focusActivityInFeed = (update: FeedUpdate, fallbackBadge: Badge | null) => {
    const targetId = (isProfileScoped ? feedFocusId : null) ?? ownId;
    if (!targetId) return;
    const key = feedUpdateKey(update);
    if (isProfileScoped && lockedProfileId === targetId) {
      pendingActivityScrollKeyRef.current = key;
      pendingActivityFallbackBadgeRef.current = fallbackBadge;
      setActivityScrollNonce((n) => n + 1);
      return;
    }
    setPendingFeedScroll(key, fallbackBadge?.id ?? null);
    openProfileFeed(targetId);
  };

  // Pick up scroll target after navigating to a profile feed (module store survives remounts).
  useEffect(() => {
    if (!isProfileScoped) return;
    const pending = getPendingFeedScroll();
    if (!pending) return;

    pendingActivityScrollKeyRef.current = pending.key;
    pendingActivityFallbackBadgeRef.current = pending.fallbackBadgeId
      ? (badgesMap.get(pending.fallbackBadgeId) ?? null)
      : null;
    setActivityScrollNonce((n) => n + 1);
  }, [isProfileScoped, lockedProfileId, badgesMap]);

  useEffect(() => {
    const key = pendingActivityScrollKeyRef.current;
    if (!key || activityScrollNonce === 0) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 24;
    const timeouts: number[] = [];

    const findCard = (): HTMLElement | null => {
      const nodes = document.querySelectorAll('[data-feed-update-id]');
      for (const node of nodes) {
        if (node instanceof HTMLElement && node.getAttribute('data-feed-update-id') === key) {
          return node;
        }
      }
      return null;
    };

    const finish = (scrollCard: HTMLElement | null) => {
      clearPendingFeedScroll();
      pendingActivityScrollKeyRef.current = null;
      const fallback = pendingActivityFallbackBadgeRef.current;
      pendingActivityFallbackBadgeRef.current = null;
      if (scrollCard) {
        scrollFeedCardIntoView(scrollCard);
        // Content-enter animation (translateY) can shift layout — correct once it settles.
        timeouts.push(
          window.setTimeout(() => {
            if (!cancelled) scrollFeedCardIntoView(scrollCard);
          }, 500)
        );
        return;
      }
      if (fallback && setSelectedBadge) setSelectedBadge(fallback);
    };

    const tryScroll = () => {
      if (cancelled) return;
      const card = findCard();
      if (card) {
        finish(card);
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        timeouts.push(window.setTimeout(tryScroll, 50));
        return;
      }

      finish(null);
    };

    // Let profile feed mount before searching for the card.
    timeouts.push(window.setTimeout(tryScroll, 100));
    return () => {
      cancelled = true;
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [filter, filtered, activityScrollNonce, setSelectedBadge]);

  useEffect(() => {
    if (isProfileScoped) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (feedCycleIds.length === 0) return;

      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      e.preventDefault();
      const anchorId = ownId;
      const currentIndex = anchorId ? feedCycleIds.indexOf(anchorId) : -1;
      let nextIndex: number;
      if (e.key === 'ArrowLeft') {
        nextIndex = currentIndex <= 0 ? feedCycleIds.length - 1 : currentIndex - 1;
      } else if (currentIndex < 0 || currentIndex >= feedCycleIds.length - 1) {
        nextIndex = 0;
      } else {
        nextIndex = currentIndex + 1;
      }
      openProfileFeed(feedCycleIds[nextIndex]);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [feedCycleIds, ownId, isProfileScoped, openProfileFeed]);

  const openProfile = (update: FeedUpdate) => {
    if (update.isCatalogAddition) return;
    // Multi-person grants: open the badge (no single profile owner).
    if ((update.grantedToProfiles?.length ?? 0) > 1) {
      const topBadge = rankBadgesByScore(allNewBadges(update), badgesMap)[0];
      if (topBadge) setSelectedBadge?.(topBadge);
      return;
    }
    // Profile Feed: open the featured claim badge's modal (stay on feed).
    if (isProfileScoped) {
      const topBadge = rankBadgesByScore(allNewBadges(update), badgesMap)[0];
      if (topBadge) setSelectedBadge?.(topBadge);
      return;
    }
    openProfileFeed(getFirstPartOfEmail(update.profile.email));
  };

  const renderCard = (update: FeedUpdate, index: number) => {
    // Profile has no rarity/topic chips — ignore any leftover shared chip state when rendering.
    const cardFilter: FeedFilterState = isProfileScoped ? { ...filter, rarity: null, topic: null } : filter;
    const badges = badgesForFilter(update, cardFilter, badgesMap);
    const timeAgo = formatLastUpdated(update.eventAt);
    // Profile feed: only the focused person — never list co-grantees from a shared auto-grant.
    const grantRecipients = (() => {
      if (isProfileScoped && feedFocusId) {
        const self =
          update.grantedToProfiles?.find((p) => getFirstPartOfEmail(p.email) === feedFocusId) ??
          (getFirstPartOfEmail(update.profile.email) === feedFocusId ? update.profile : null);
        return self ? [self] : [];
      }
      if (update.grantedToProfiles?.length) return update.grantedToProfiles;
      return update.profile ? [update.profile] : [];
    })();
    const profileName =
      grantRecipients.length > 0 ? (
        joinRecipientNames(
          grantRecipients.map((p) => (
            <FeedProfileName
              key={p.email}
              name={p.name}
              profileId={getFirstPartOfEmail(p.email)}
              onOpen={openProfileCollected}
            />
          ))
        )
      ) : (
        <strong className="feed-profile-name">Someone</strong>
      );
    const updateId = feedUpdateKey(update);
    const key = `${updateId}-${filterKey(filter)}`;
    const enterStyle = {
      ['--feed-enter-delay' as string]: `${Math.min(index, 24) * 28}ms`,
    };

    if (update.isCatalogAddition) {
      const count = badges.length;
      const submitterName = isProfileScoped ? focusedProfile?.name : null;
      return (
        <article
          key={key}
          data-feed-update-id={updateId}
          className="feed-card feed-card--catalog clickable"
          style={enterStyle}
          onClick={() => {
            if (badges[0]) setSelectedBadge?.(badges[0]);
          }}
          {...cardHighlightProps(update)}
        >
          <div className="feed-card-main">
            <FeedCardTopline
              headline={
                submitterName && feedFocusId ? (
                  <>
                    <FeedProfileName name={submitterName} profileId={feedFocusId} onOpen={openProfileCollected} />{' '}
                    contributed{' '}
                    <strong>
                      {count} new badge{count === 1 ? '' : 's'}
                    </strong>
                  </>
                ) : (
                  catalogAdditionHeadline(count)
                )
              }
              timeAgo={timeAgo}
              withLogo={!(submitterName && feedFocusId)}
            />
            <div className="feed-desc-stack">
              {badges.length <= 4 ? (
                badges.map((badge) => (
                  <FeedBadgeExplain
                    key={badge.id}
                    badge={badge}
                    onSelect={setSelectedBadge}
                    profilesMap={profilesMap}
                    showCredits
                  />
                ))
              ) : (
                <>
                  {badges.slice(0, 3).map((badge) => (
                    <FeedBadgeExplain
                      key={badge.id}
                      badge={badge}
                      onSelect={setSelectedBadge}
                      profilesMap={profilesMap}
                      showCredits
                    />
                  ))}
                  <FeedCompactBadgeRow badges={badges.slice(3)} onSelect={setSelectedBadge} />
                </>
              )}
            </div>
          </div>
        </article>
      );
    }

    // Multi-person grants ("to N profiles" / named list) — journey notes are ambiguous.
    const hideJourneyForMultiGrant = grantRecipients.length > 1;
    const journeys = hideJourneyForMultiGrant
      ? []
      : journeysMatchingRarity(update.completedJourneys, cardFilter.rarity).sort(
          (a, b) => rarityOrderIndex(a.rarity) - rarityOrderIndex(b.rarity)
        );
    const hasJourneys = journeys.length > 0;
    // Journey-only framing is for the Journeys chip; default/profile feed shows full claims.
    const showAsJourneyCard = hasJourneys && cardFilter.topic === 'journeys';
    // badgesForFilter already narrows to journey-path badges when topic is journeys.
    const displayBadges = badges;
    // Golden only for legendary+ journeys or legendary+ badges shown on this card
    // (not other badges that may sit on the same update but are filtered out of view).
    const isMilestone =
      journeys.some((journey) => rarityOrderIndex(journey.rarity) <= rarityOrderIndex('legendary')) ||
      displayBadges.some((badge) => rarityOrderIndex(badge.rarityName) <= rarityOrderIndex('legendary'));
    const openProfileJourney = (journeyId: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      const id = getFirstPartOfEmail(update.profile.email);
      setSelectedProfileID(id);
      navigate(`/${id}/journeys#${journeyId}`);
    };

    const journeyNote = hasJourneys ? (
      <div className="feed-journey-note">
        {journeys.map((journey) => (
          <button
            key={journey.id}
            type="button"
            className={`feed-card-meta feed-journey-link rarity-${journey.rarity}`}
            onClick={(e) => openProfileJourney(journey.id, e)}
          >
            <span className={`feed-status-dot rarity-${journey.rarity}`} />
            <span>
              Completed {journey.emoji ? `${journey.emoji} ` : ''}
              {journey.name}
            </span>
          </button>
        ))}
      </div>
    ) : null;
    const cardClass = `feed-card clickable${isMilestone ? ' feed-card-milestone' : ''}`;

    const journeyHeadline =
      journeys.length === 1 ? (
        <>
          {profileName} completed{' '}
          <strong>
            {journeys[0].emoji ? `${journeys[0].emoji} ` : ''}
            {journeys[0].name}
          </strong>
        </>
      ) : (
        <>
          {profileName} completed <strong>{journeys.length} journeys</strong>
        </>
      );

    // Journeys filter only: journey headline + related badges.
    if (showAsJourneyCard) {
      return (
        <article
          key={key}
          data-feed-update-id={updateId}
          className={cardClass}
          style={enterStyle}
          onClick={() => openProfileJourney(journeys[0].id)}
          {...cardHighlightProps(update)}
        >
          <div className="feed-card-main">
            <div className="feed-card-topline">
              <p className="feed-card-headline">{journeyHeadline}</p>
              <span className="feed-timestamp">{timeAgo}</span>
            </div>
            <div className="feed-desc-stack">
              {displayBadges.length > 0 && displayBadges.length <= 5
                ? displayBadges.map((badge) => (
                    <FeedBadgeExplain key={badge.id} badge={badge} onSelect={setSelectedBadge} />
                  ))
                : null}
              {displayBadges.length >= 6 ? (
                <>
                  <FeedBadgeExplain badge={displayBadges[0]} onSelect={setSelectedBadge} />
                  <FeedCompactBadgeRow badges={displayBadges.slice(1)} onSelect={setSelectedBadge} />
                </>
              ) : null}
              {journeyNote}
            </div>
          </div>
        </article>
      );
    }

    // 1–3 badges with stacked descriptions (claims, joins, and system auto-grants)
    const useJoinLingo = update.isNewUser && cardFilter.rarity == null;
    const useGrantLingo = !!update.isAutoGrant && !useJoinLingo;
    if (displayBadges.length === 0) {
      return (
        <article
          key={key}
          data-feed-update-id={updateId}
          className={cardClass}
          style={enterStyle}
          onClick={() => openProfile(update)}
          {...cardHighlightProps(update)}
        >
          <div className="feed-card-main">
            <FeedCardTopline headline={<>{profileName} updated their badges</>} timeAgo={timeAgo} />
            {journeyNote ? <div className="feed-desc-stack">{journeyNote}</div> : null}
          </div>
        </article>
      );
    }
    if (displayBadges.length <= 3) {
      const headline = useJoinLingo ? (
        <>
          {profileName} joined Talos Badges with{' '}
          <strong>
            {displayBadges.length} badge{displayBadges.length !== 1 ? 's' : ''}
          </strong>
        </>
      ) : useGrantLingo ? (
        autoGrantHeadline(profileName, displayBadges, setSelectedBadge, grantRecipients.length)
      ) : displayBadges.length === 1 ? (
        <>
          {profileName} claimed <FeedBadgeName badge={displayBadges[0]} onSelect={setSelectedBadge} />
        </>
      ) : displayBadges.length === 2 ? (
        <>
          {profileName} claimed <FeedBadgeName badge={displayBadges[0]} onSelect={setSelectedBadge} /> and{' '}
          <FeedBadgeName badge={displayBadges[1]} onSelect={setSelectedBadge} />
        </>
      ) : (
        <>
          {profileName} claimed <FeedBadgeName badge={displayBadges[0]} onSelect={setSelectedBadge} />,{' '}
          <FeedBadgeName badge={displayBadges[1]} onSelect={setSelectedBadge} />, and{' '}
          <FeedBadgeName badge={displayBadges[2]} onSelect={setSelectedBadge} />
        </>
      );

      return (
        <article
          key={key}
          data-feed-update-id={updateId}
          className={cardClass}
          style={enterStyle}
          onClick={() => openProfile(update)}
          {...cardHighlightProps(update)}
        >
          <div className="feed-card-main">
            <FeedCardTopline headline={headline} timeAgo={timeAgo} withLogo={useGrantLingo} />
            <div className="feed-desc-stack">
              {displayBadges.map((badge) => (
                <FeedBadgeExplain key={badge.id} badge={badge} onSelect={setSelectedBadge} />
              ))}
              {journeyNote}
            </div>
          </div>
        </article>
      );
    }

    // 4+ badges: coolest explained on top, remaining as tinys underneath
    const [topBadge, ...restBadges] = displayBadges;
    const headline = useJoinLingo ? (
      <>
        {profileName} joined Talos Badges with <strong>{displayBadges.length} badges</strong>
      </>
    ) : useGrantLingo ? (
      autoGrantHeadline(profileName, displayBadges, setSelectedBadge, grantRecipients.length)
    ) : (
      <>
        {profileName} claimed <strong>{displayBadges.length} new badges</strong>
      </>
    );

    return (
      <article
        key={key}
        data-feed-update-id={updateId}
        className={cardClass}
        style={enterStyle}
        onClick={() => openProfile(update)}
        {...cardHighlightProps(update)}
      >
        <div className="feed-card-main">
          <FeedCardTopline headline={headline} timeAgo={timeAgo} withLogo={useGrantLingo} />
          <div className="feed-desc-stack">
            <FeedBadgeExplain badge={topBadge} onSelect={setSelectedBadge} />
            {restBadges.length > 0 ? <FeedCompactBadgeRow badges={restBadges} onSelect={setSelectedBadge} /> : null}
            {journeyNote}
          </div>
        </div>
      </article>
    );
  };

  const feedFilters = (
    <div className="feed-filters" aria-label="Feed filters">
      <div className="feed-filters-pills">
        <div className="feed-filter-group" role="group" aria-label="Time range">
          {(
            [
              ['week', 'Last Week'],
              ['year', `In ${new Date().getFullYear()}`],
            ] as const
          ).map(([id, label]) => (
            <button
              key={`time-${id}`}
              type="button"
              aria-pressed={filter.range === id}
              className={`feed-filter-chip${filter.range === id ? ' active' : ''}`}
              onClick={() => setTimeRange(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="feed-filter-group" role="group" aria-label="Rarity">
          {(
            [
              ['rare', 'Rare+'],
              ['legendary', 'Legendary+'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={`rarity-${id}`}
              type="button"
              aria-pressed={filter.rarity === id}
              className={`feed-filter-chip feed-filter-chip--${id}${filter.rarity === id ? ' active' : ''}`}
              onClick={() => setRarity(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="feed-filter-group" role="group" aria-label="Topic">
          {(
            [
              ['journeys', 'Journeys'],
              ['newBadges', 'New Badges'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={`topic-${id}`}
              type="button"
              aria-pressed={filter.topic === id}
              className={`feed-filter-chip${filter.topic === id ? ' active' : ''}`}
              onClick={() => setTopic(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {onRefresh ? (
        <button
          type="button"
          className={`feed-refresh-button${isRefreshing ? ' is-refreshing' : ''}${refreshDone ? ' is-done' : ''}`}
          aria-label={refreshDone ? 'Refresh complete' : 'Refresh feed'}
          title={refreshDone ? 'Up to date' : 'Refresh profiles and feed'}
          disabled={isRefreshing || refreshDone}
          onClick={() => {
            void onRefresh();
          }}
        >
          <span className="feed-refresh-label" key={refreshDone ? 'done' : isRefreshing ? 'busy' : 'idle'}>
            {refreshDone ? (
              'Done'
            ) : (
              <>
                <span className="feed-refresh-icon" aria-hidden>
                  ↻
                </span>
                Refresh
              </>
            )}
          </span>
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="feed-layout">
      {isProfileScoped ? null : (
        <header className="feed-layout-header gallery-header">
          <ProfileIdentitySearch
            selectedProfileId={null}
            onSelectProfile={openProfileFeed}
            onClear={() => {
              scrollFeedMainToTop();
              setFeedFocusId(null);
            }}
            avatarSrc={talosBadgesLogoURL}
          >
            <h1 className="gallery-title">Badges Feed</h1>
            <p className="gallery-subtitle">{feedSubtitle}</p>
          </ProfileIdentitySearch>
        </header>
      )}

      <div className={isProfileScoped ? 'feed-page profile-feed-body' : 'feed-page'}>
        {isProfileScoped ? null : feedFilters}
        <div className="feed-list">
          {filtered.length === 0 ? (
            <p className="feed-empty">
              {isProfileScoped && focusedProfile
                ? filter.topic === 'newBadges'
                  ? `No badge submissions from ${focusedProfile.name} in this range.`
                  : `No matching feed activity for ${focusedProfile.name}.`
                : filter.rarity != null || filter.topic != null
                  ? 'Nothing matches this filter right now.'
                  : filter.range === 'week'
                    ? 'No updates since last week.'
                    : filter.range === 'year'
                      ? `No updates in ${new Date().getFullYear()}.`
                      : 'No feed activity yet.'}
            </p>
          ) : (
            filtered.map((update, index) => renderCard(update, index))
          )}
        </div>
      </div>

      <FeedAside
        currentProfile={currentProfile}
        focusedProfileId={isProfileScoped ? feedFocusId : null}
        onFocusProfile={openProfileFeed}
        onSelectActivity={focusActivityInFeed}
        onSelectProfile={openProfileFeed}
        setSelectedBadge={setSelectedBadge}
      />
    </div>
  );
};
